import { effect, Injectable, signal } from '@angular/core';

/**
 * @description Servicio para gestionar el tema de la aplicación usando signals
 * para una mejor reactividad y sincronización automática con el DOM
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  /**
   * @description Signal que controla si el modo oscuro está activo
   */
  private readonly _isDarkMode = signal<boolean>(false);

  /**
   * @description Signal de solo lectura para acceder al estado del tema
   */
  public readonly isDarkMode = this._isDarkMode.asReadonly();

  constructor() {
    // Effect que sincroniza automáticamente el estado del tema con el DOM
    effect(() => {
      const darkMode = this._isDarkMode();
      if (darkMode) {
        document.documentElement.classList.add('dark');
        localStorage.setItem('theme', 'dark');
      } else {
        document.documentElement.classList.remove('dark');
        localStorage.setItem('theme', 'light');
      }
    });
  }

  /**
   * @description Inicializa el tema basado en las preferencias guardadas o del sistema
   */
  initializeTheme(): void {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)',
    ).matches;

    const shouldUseDarkMode =
      savedTheme === 'dark' || (!savedTheme && prefersDark);
    this._isDarkMode.set(shouldUseDarkMode);
  }

  /**
   * @description Alterna entre el modo oscuro y claro
   * El effect se encarga automáticamente de actualizar el DOM y localStorage
   */
  toggleDarkMode(): void {
    this._isDarkMode.update((current) => !current);
  }

  /**
   * @description Establece el modo de tema específico
   * @param {boolean} isDark - true para modo oscuro, false para modo claro
   */
  setDarkMode(isDark: boolean): void {
    this._isDarkMode.set(isDark);
  }
}
